import { dsColorNew } from "@palette"

const COLORS = {
	GOOD: dsColorNew.successText,
	WARN: dsColorNew.noteAmber,
	BAD: dsColorNew.dangerRed,
}

const EMPTY = '-'

const isNumber = value => typeof value === 'number' && !Number.isNaN(value)

/**
 * Picks a semaphore color from ordered thresholds.
 * `thresholds` is [[limit, color], ...] evaluated in order; the last entry is
 * the fallback when nothing matched.
 */
const pickColor = (value, thresholds, fallback) => {
	if (!isNumber(value)) return undefined

	const matched = thresholds.find(([limit, , compare]) =>
		compare === 'gte' ? value >= limit : value <= limit)

	return matched ? matched[1] : fallback
}

const signedAmount = (value, currency) => {
	if (!isNumber(value)) return EMPTY

	const sign = value >= 0 ? '+' : '−'
	return `${sign}${Math.abs(value).toFixed(2)}${currency ? ` ${currency}` : ''}`
}

/**
 * Maps the backend `mainData` block onto the five header KPIs.
 *
 * Every field tolerates `null`: an execution without a main provider has no
 * positions, so the backend sends nulls rather than NaN.
 */
export const buildPositionKpis = (mainData, currency) => {
	if (!mainData || !Object.keys(mainData).length) return []

	const { avgMarketRank, top3Share, priceIndex, gapCheapest, gapAverage } = mainData

	const rank = avgMarketRank?.value
	const cheapestPct = avgMarketRank?.cheapestPercentage
	const top3 = top3Share?.value

	return [{
		key: 'avgMarketRank',
		label: 'Avg market rank',
		value: isNumber(rank) ? rank.toFixed(1) : EMPTY,
		unit: avgMarketRank?.lenCompetitors ? `/ ${avgMarketRank.lenCompetitors}` : '',
		note: isNumber(cheapestPct) ? `cheapest on ${cheapestPct.toFixed(0)}% of dates` : 'no main provider selected',
		color: pickColor(rank, [[2, COLORS.GOOD], [3.5, COLORS.WARN]], COLORS.BAD),
	}, {
		key: 'priceIndex',
		label: 'Price index',
		value: isNumber(priceIndex) ? priceIndex.toFixed(0) : EMPTY,
		note: isNumber(priceIndex)
			? `${priceIndex >= 100 ? '+' : ''}${(priceIndex - 100).toFixed(1)}% vs market average`
			: 'no market reference',
		color: pickColor(priceIndex, [[100, COLORS.GOOD], [105, COLORS.WARN]], COLORS.BAD),
		// Above market reads as a warning, below it as an advantage.
		noteColor: isNumber(priceIndex)
			? (priceIndex > 100 ? COLORS.BAD : COLORS.GOOD)
			: undefined,
	}, {
		key: 'top3Share',
		label: 'Top-3 share',
		value: isNumber(top3) ? top3.toFixed(0) : EMPTY,
		unit: isNumber(top3) ? '%' : '',
		note: 'of dates in the cheapest 3',
		color: pickColor(top3, [[60, COLORS.GOOD, 'gte'], [30, COLORS.WARN, 'gte']], COLORS.BAD),
	}, {
		key: 'gapCheapest',
		label: 'Gap to cheapest',
		value: signedAmount(gapCheapest, currency),
		note: isNumber(gapCheapest) && gapCheapest > 0
			? 'avg above the daily cheapest'
			: 'avg lead vs the cheapest rival',
		color: pickColor(gapCheapest, [[0, COLORS.GOOD], [15, COLORS.WARN]], COLORS.BAD),
	}, {
		key: 'gapAverage',
		label: 'Gap to average',
		value: signedAmount(gapAverage, currency),
		note: 'avg vs the market average',
		color: pickColor(gapAverage, [[0, COLORS.GOOD]], COLORS.WARN),
	}]
}
